<template>
    <div class="chart-container">
        <div class="chart-controls mb-3">
            <div class="d-flex justify-content-between align-items-center">
                <div class="metric-tabs">
                    <button 
                        v-for="metric in metrics" 
                        :key="metric.key"
                        @click="selectedMetric = metric.key"
                        :class="['btn', 'btn-sm', 'me-2', selectedMetric === metric.key ? 'btn-primary' : 'btn-outline-primary']"
                    >
                        {{ metric.label }}
                    </button>
                </div>
                <div class="chart-actions">
                    <button @click="resetZoom" class="btn btn-sm btn-secondary">Reset Zoom</button>
                </div>
            </div>
        </div>
        
        <div class="chart-wrapper" style="height: 400px;">
            <canvas ref="chartCanvas"></canvas>
        </div>
        
        <div class="mt-2 text-muted">
            <div v-if="selectedRange">
                <small>Selected Range: Slot {{ selectedRange.min }} - {{ selectedRange.max }}</small>
            </div>
            <div v-if="rawData.length > 0">
                <small>Data Granularity: {{ rawData[0].granularity }} | {{ rawData.length }} data points</small>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

Chart.register(...registerables, zoomPlugin);

export default {
    name: 'BlockTimeSeriesChart',
    props: {
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['range-selected'],
    data() {
        return {
            chart: null,
            rawData: [],
            selectedMetric: 'rewards',
            selectedRange: null,
            isLoading: false,
            metrics: [
                { key: 'rewards', label: 'Validator Rewards', color: '#9945ff' },
                { key: 'jito_total', label: 'Jito Tips', color: '#608aff' },
                { key: 'block_duration', label: 'Block Duration (ms)', color: '#5cb7ff' },
                { key: 'cu', label: 'Compute Units', color: '#9adaff' },
                { key: 'non_votes', label: 'Non-Vote Transactions', color: '#e9f6fb' },
                { key: 'priority_fees', label: 'Priority Fees', color: '#b2f7ff' },
            ]
        }
    },
    mounted() {
        this.initChart();
        if (Object.keys(this.filters).length > 0) {
            this.loadData();
        }
    },
    watch: {
        selectedMetric() {
            this.updateChart();
        },
        filters: {
            handler() {
                this.loadData();
            },
            deep: true
        }
    },
    methods: {
        async loadData() {
            if (this.isLoading || Object.keys(this.filters).length === 0) {
                return;
            }
            
            this.isLoading = true;
            
            try {
                const params = {};
                
                for (const [key, value] of Object.entries(this.filters)) {
                    if (value !== null && value !== undefined) {
                        if (Array.isArray(value)) {
                            params[`filter[${key}]`] = value.join(',');
                        } else if (typeof value === 'object') {
                            params[`filter[${key}]`] = JSON.stringify(value);
                        } else {
                            params[`filter[${key}]`] = String(value);
                        }
                    }
                }
                
                const response = await axios.get('/blocks/chart-data', { params });
                
                // Store raw data as plain JavaScript objects
                this.rawData = Array.isArray(response.data) ? 
                    JSON.parse(JSON.stringify(response.data)) : [];
                
                this.updateChart();
            } catch (error) {
                console.error('Error loading chart data:', error);
                this.rawData = [];
                this.updateChart();
            } finally {
                this.isLoading = false;
            }
        },
        
        initChart() {
            const ctx = this.$refs.chartCanvas.getContext('2d');
            
            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets: []
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: 'index'
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            position: 'bottom',
                            title: {
                                display: true,
                                text: 'Time'
                            }
                        },
                        y: {
                            title: {
                                display: true,
                                text: this.getCurrentMetricLabel()
                            }
                        }
                    },
                    plugins: {
                        zoom: {
                            pan: {
                                enabled: true,
                                mode: 'x',
                                threshold: 10,
                            },
                            zoom: {
                                wheel: {
                                    enabled: true,
                                },
                                pinch: {
                                    enabled: true
                                },
                                mode: 'x',
                                onZoomComplete: (context) => {
                                    const chart = context.chart;
                                    const xScale = chart.scales.x;
                                    
                                    let minSlot = null;
                                    let maxSlot = null;
                                    
                                    for (let i = 0; i < this.rawData.length; i++) {
                                        const dataPoint = this.rawData[i];
                                        if (dataPoint.timestamp >= xScale.min && dataPoint.timestamp <= xScale.max) {
                                            if (minSlot === null || dataPoint.slot < minSlot) {
                                                minSlot = dataPoint.slot;
                                            }
                                            if (maxSlot === null || dataPoint.slot > maxSlot) {
                                                maxSlot = dataPoint.slot;
                                            }
                                        }
                                    }
                                    
                                    if (minSlot !== null && maxSlot !== null) {
                                        this.selectedRange = { min: minSlot, max: maxSlot };
                                        this.$emit('range-selected', this.selectedRange);
                                    }
                                }
                            }
                        },
                        legend: {
                            display: false
                        },
                        tooltip: {
                            callbacks: {
                                title: (context) => {
                                    const dataIndex = context[0]?.dataIndex;
                                    if (dataIndex === undefined || !this.rawData[dataIndex]) return '';
                                    
                                    const dataPoint = this.rawData[dataIndex];
                                    if (dataPoint.slot_range) {
                                        return `Slots ${dataPoint.slot_range[0]} - ${dataPoint.slot_range[1]}`;
                                    }
                                    return `Slot ${dataPoint.slot}`;
                                },
                                afterTitle: (context) => {
                                    const dataIndex = context[0]?.dataIndex;
                                    if (dataIndex === undefined || !this.rawData[dataIndex]) return '';
                                    
                                    const dataPoint = this.rawData[dataIndex];
                                    return dataPoint.granularity ? `Granularity: ${dataPoint.granularity}` : '';
                                }
                            }
                        }
                    }
                }
            });
        },
        
        updateChart() {
            if (!this.chart || !this.rawData.length) {
                if (this.chart) {
                    this.chart.data.datasets = [];
                    this.chart.update('none');
                }
                return;
            }
            
            const metric = this.metrics.find(m => m.key === this.selectedMetric);
            
            // Create simple data arrays - no reactive objects
            const mainData = [];
            const skippedData = [];
            
            for (let i = 0; i < this.rawData.length; i++) {
                const block = this.rawData[i];
                const value = this.getMetricValue(block, this.selectedMetric);
                
                if (value !== null) {
                    mainData.push({
                        x: block.timestamp,
                        y: value
                    });
                }
                
                // Add skipped blocks for relevant metrics
                if (['rewards', 'block_duration', 'cu'].includes(this.selectedMetric) && 
                    (block.skipped || block.skipped_count > 0)) {
                    skippedData.push({
                        x: block.timestamp,
                        y: value || 0
                    });
                }
            }
            
            // Create datasets as plain objects
            const datasets = [
                {
                    label: metric.label,
                    data: mainData,
                    borderColor: metric.color,
                    backgroundColor: metric.color + '20',
                    borderWidth: 2,
                    pointRadius: mainData.length > 1000 ? 0 : 1,
                    pointHoverRadius: 4,
                    fill: false
                }
            ];
            
            if (skippedData.length > 0) {
                datasets.push({
                    label: 'Skipped Blocks',
                    data: skippedData,
                    borderColor: '#ff4444',
                    backgroundColor: '#ff4444',
                    pointRadius: 3,
                    pointHoverRadius: 6,
                    showLine: false,
                    pointStyle: 'triangle'
                });
            }
            
            // Completely replace chart data
            this.chart.data = { datasets: datasets };
            this.chart.options.scales.y.title.text = metric.label;
            this.chart.update('none');
        },
        
        getMetricValue(block, metricKey) {
            const rawValue = block[metricKey];
            const value = parseFloat(rawValue);
            
            if (isNaN(value)) return null;
            
            switch (metricKey) {
                case 'block_duration':
                    return value / 1000000; // Convert nanoseconds to ms
                case 'cu':
                    return value / 1000000; // Convert to millions
                case 'rewards':
                case 'jito_total':
                case 'priority_fees':
                    return value / 1000000000; // Convert lamports to SOL
                default:
                    return value;
            }
        },
        
        getCurrentMetricLabel() {
            const metric = this.metrics.find(m => m.key === this.selectedMetric);
            return metric ? metric.label : '';
        },
        
        resetZoom() {
            if (this.chart) {
                this.chart.resetZoom();
                this.selectedRange = null;
                this.$emit('range-selected', null);
            }
        }
    },
    
    beforeUnmount() {
        if (this.chart) {
            this.chart.destroy();
        }
    }
}
</script>

<style scoped>
.chart-container {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.metric-tabs button {
    transition: all 0.2s ease;
}

.chart-wrapper {
    position: relative;
}
</style>
